/**
 * Shared context-routing for the right panel's Events and Alerts tabs.
 *
 * Both feeds auto-scope to the active context (spec §23.3, mockup `filterAlerts`
 * / `_rpMatchesCtx`) — there are no manual context chips. Events and Alerts are
 * genuinely distinct feeds off different topics (FLYsafe MQTT Topic doc §9 +
 * Appendix A): Events is the raw operational log including logged-only `ok`
 * items, Alerts is the Alert-Router's curated, ack-able subset. They share only
 * this routing rule.
 */
import type { Context, TabId } from '@/lib/store/ui';

export const CATEGORY_TO_TAB: Record<string, TabId> = {
  DAA: 'ldt-daa',
  Surveillance: 'ldt-daa',
  Power: 'ldt-flight',
  C2: 'ldt-c2',
  Comms: 'ldt-c2',
  Mission: 'ldt-flight',
  System: 'ldt-status',
  OI: 'ldt-status',
  Infrastructure: 'ldt-status',
};

export const DRONE_CATEGORIES = new Set(['DAA', 'C2', 'Power', 'Mission', 'System', 'Comms']);

export const CTX_LABEL: Record<Context, string> = {
  drones: 'Drones',
  oi: 'Active OI',
  flights: 'Flights',
  surv: 'Active Surveillance',
  infra: 'Infrastructure',
  rtm: 'RTM Services',
  inv: 'Inventory',
  ds: 'Data Stream',
  apps: 'Apps',
};

type RoutableItem = {
  category: string;
  drone_id: string | null | undefined;
  contexts?: string[];
};

/**
 * Does this alert/event belong to `ctx`?
 *
 * Prefers the BE's own `contexts` field (mapped to our nav-context ids via
 * `lib/mqtt/contextMap.ts`) when the producing job sets one. Falls back to
 * the old category-based guess for jobs that don't set it yet
 * (am-nid-job, am-nid-dd-job per the 2026-08-13 job/category mapping) —
 * this is why `contexts` is additive: once a job starts sending it, this
 * function picks it up with no further code change.
 *
 * inv / apps have no category in the canonical EventCategory enum, so their
 * Events / Alerts tabs render empty until the backend publishes one — the tabs
 * exist for structural parity with the mockup, not to show invented data.
 */
export function routeByContext(item: RoutableItem, ctx: Context, selectedDroneId: string | null): boolean {
  if (item.contexts && item.contexts.length > 0) {
    if (!item.contexts.includes(ctx)) return false;
    if (ctx === 'drones') {
      if (!item.drone_id) return false;
      if (selectedDroneId && item.drone_id !== selectedDroneId) return false;
    }
    return true;
  }
  return legacyRouteByContext(item, ctx, selectedDroneId);
}

function legacyRouteByContext(
  item: { category: string; drone_id: string | null | undefined },
  ctx: Context,
  selectedDroneId: string | null,
): boolean {
  if (ctx === 'drones') {
    if (!item.drone_id) return false;
    if (!DRONE_CATEGORIES.has(item.category)) return false;
    if (selectedDroneId && item.drone_id !== selectedDroneId) return false;
    return true;
  }
  if (ctx === 'surv') return item.category === 'Surveillance' || item.category === 'DAA';
  if (ctx === 'infra') return item.category === 'Infrastructure';
  if (ctx === 'rtm') return item.category === 'System' && item.drone_id == null;
  if (ctx === 'oi') return item.category === 'OI';
  if (ctx === 'inv' || ctx === 'apps') return false;
  return true;
}
